import { addDays, formatISO, getDay, startOfDay, startOfWeek } from 'date-fns';
import type { Booking, ConfirmationCall, StudioId } from '@/domain/types';
import { confirmationDeadlineState } from '@/utils/confirmationTemplates';

// ---------------------------------------------------------------------------
// Confirmation cadence (vault System Diagram 06). Appointment days pair up -
// (Mon+Tue), (Wed+Thu), (Fri+Sat). Each booking gets three call sequences on
// three straight working days, worked from a Thursday ~10 days ahead, skipping
// Sundays; the ops decision falls the next working day. After the 3rd
// unanswered sequence the booking escalates to Ops (routed to `chase` here).
// Short-notice / gap-fill bookings skip the cadence (no room for it).
// ---------------------------------------------------------------------------

export type ConfirmationPair = 'mon-tue' | 'wed-thu' | 'fri-sat';

export interface ConfirmationSchedule {
  pair: ConfirmationPair;
  /** Start-of-day ISO anchors for the three sequence days + the ops decision day. */
  seq1Iso: string;
  seq2Iso: string;
  seq3Iso: string;
  decisionIso: string;
}

export type ConfirmationLane = 'seq-1' | 'seq-2' | 'seq-3' | 'two-day' | 'day-of';

const SEQ_LANES: readonly ConfirmationLane[] = ['seq-1', 'seq-2', 'seq-3'];
const PAIR_BY_INDEX: readonly ConfirmationPair[] = ['mon-tue', 'wed-thu', 'fri-sat'];

/** Which paired-day bucket (0..2) an appointment day-of-week falls into. */
function pairIndexForDow(dow: number): number {
  if (dow === 1 || dow === 2) return 0; // Mon/Tue
  if (dow === 3 || dow === 4) return 1; // Wed/Thu
  if (dow === 5 || dow === 6) return 2; // Fri/Sat
  return 0; // Sunday fallback - no Sunday appointments in the demo.
}

/**
 * Advance `n` calendar days from `d`; after each +1, if it lands on a Sunday
 * skip one more day. Saturday IS a working day - only Sundays are skipped.
 */
function addWorkingDays(d: Date, n: number): Date {
  let cur = d;
  for (let i = 0; i < n; i += 1) {
    cur = addDays(cur, 1);
    if (getDay(cur) === 0) cur = addDays(cur, 1);
  }
  return cur;
}

/**
 * The three sequence days + ops decision day for a booking's appointment,
 * derived purely from `booking.start`. See the module header for the model.
 */
export function confirmationSchedule(booking: Booking): ConfirmationSchedule {
  const start = startOfDay(new Date(booking.start));
  const pairIndex = pairIndexForDow(getDay(start));
  const weekMonday = startOfWeek(start, { weekStartsOn: 1 });
  const baseThursday = addDays(weekMonday, -11); // always a Thursday
  const seq1 = addWorkingDays(baseThursday, pairIndex);
  const seq2 = addWorkingDays(seq1, 1);
  const seq3 = addWorkingDays(seq2, 1);
  const decision = addWorkingDays(seq3, 1);
  return {
    pair: PAIR_BY_INDEX[pairIndex]!,
    seq1Iso: formatISO(seq1),
    seq2Iso: formatISO(seq2),
    seq3Iso: formatISO(seq3),
    decisionIso: formatISO(decision),
  };
}

/**
 * A short-notice / gap-fill booking created on/after its own first sequence day
 * has no room for the cadence and skips it (it is auto-confirmed at booking).
 */
export function isShortNoticeBooking(booking: Booking): boolean {
  const seq1 = startOfDay(new Date(confirmationSchedule(booking).seq1Iso)).getTime();
  const created = startOfDay(new Date(booking.createdAt)).getTime();
  return created >= seq1;
}

/**
 * Which confirmation lane a booking's work is due in on the workday `nowIso`,
 * or null. Pure + date-only. The appointment day itself is always `day-of`.
 * A confirmed booking only needs the 2-day reminder. Otherwise the next
 * unattempted sequence is due once its (roll-forward) sequence day is reached;
 * once all three are exhausted the booking has escalated to Ops (returned as
 * null here - it is no longer a call lane, see `buildConfirmationDay`).
 */
export function confirmationWorkDue(booking: Booking, nowIso: string): ConfirmationLane | null {
  const now0 = startOfDay(new Date(nowIso)).getTime();
  const start = new Date(booking.start);
  const start0 = startOfDay(start).getTime();

  if (now0 === start0) return 'day-of';

  // A Sweeper gap-fill confirmed on the call bypasses the cadence entirely
  // (bd tv-pt8): it is never a call/text lane, only the day-of floor concern.
  if (booking.confirmedOnCall) return null;

  if (booking.confirmedAt) {
    // Confirmed bookings only need the 2-day reminder.
    if (now0 === startOfDay(addDays(start, -2)).getTime()) return 'two-day';
    return null;
  }

  if (isShortNoticeBooking(booking)) return null; // gap-fill / short-notice skip cadence

  const attempts = booking.confirmationAttempts ?? 0;
  if (attempts >= 3) return null; // cadence exhausted -> escalation, not a call lane

  const s = confirmationSchedule(booking);
  const dueSeqIso = [s.seq1Iso, s.seq2Iso, s.seq3Iso][attempts]!; // next sequence to attempt
  if (now0 >= startOfDay(new Date(dueSeqIso)).getTime()) return SEQ_LANES[attempts]!;
  return null;
}

/**
 * The booking has escalated to Ops: three confirmation sequences went unanswered
 * and it is still unconfirmed. This is the flag the Ops worklist consumes; it is
 * raised as soon as the 3rd sequence is unanswered (not gated on the decision
 * day, so an escalated booking never falls out of every lane for a day).
 */
export function isConfirmationEscalated(booking: Booking): boolean {
  return !booking.confirmedAt && (booking.confirmationAttempts ?? 0) >= 3;
}

export interface ConfirmationRow {
  booking: Booking;
  lane: ConfirmationLane | null;
  /**
   * Informational: the booking is unconfirmed and inside the refillable /
   * deadline-approaching window per `confirmationDeadlineState`. Does not by
   * itself route the row to `chase` - see `overdue`.
   */
  atRisk: boolean;
  /**
   * Routes the row to `chase`: the cadence has escalated (3 unanswered
   * sequences past the decision day), a scheduled touch is stale (its
   * `scheduledFor` has passed with no recorded outcome), or the booking's day
   * is today-or-earlier and it is still unconfirmed (day-of catch-up).
   */
  overdue: boolean;
  call?: ConfirmationCall;
}

export interface ConfirmationDay {
  chase: ConfirmationRow[];
  calls: ConfirmationRow[];
  texts: ConfirmationRow[];
  counts: { total: number; chase: number; calls: number; texts: number };
}

const LIVE_STATUSES = new Set<Booking['status']>(['held', 'pending-deposit', 'confirmed']);

/** 7 day anchors (ISO, local offset preserved) starting from `nowIso`'s day. */
export function weekDayStrip(nowIso: string): string[] {
  const base = startOfDay(new Date(nowIso));
  return Array.from({ length: 7 }, (_, i) => formatISO(addDays(base, i)));
}

/**
 * Group a day's live bookings into the confirmations worklist buckets.
 * Pure: takes plain records in, returns plain rows out - no store reads.
 *
 * - `chase`: escalated (3 unanswered sequences), a stale scheduled touch, or an
 *   unconfirmed booking whose day has arrived.
 * - `calls`: an unconfirmed booking with a sequence call (seq-1/2/3) due today.
 * - `texts`: a confirmed booking's 2-day reminder.
 * Already-resolved bookings, and confirmed day-of sessions (day-of arrival is the
 * floor's job, not the confirmations team's), are dropped from the worklist.
 */
export function buildConfirmationDay(args: {
  nowIso: string;
  bookings: Record<string, Booking>;
  confirmationCalls: Record<string, ConfirmationCall>;
  studioIds: StudioId[];
}): ConfirmationDay {
  const { nowIso, bookings, confirmationCalls, studioIds } = args;
  const inScope = (b: Booking) => studioIds.length === 0 || studioIds.includes(b.studioId);
  const callByBooking = new Map<string, ConfirmationCall>();
  for (const c of Object.values(confirmationCalls)) callByBooking.set(c.bookingId, c);

  const chase: ConfirmationRow[] = [];
  const calls: ConfirmationRow[] = [];
  const texts: ConfirmationRow[] = [];
  const today0 = startOfDay(new Date(nowIso)).getTime();

  for (const b of Object.values(bookings)) {
    if (!inScope(b) || !LIVE_STATUSES.has(b.status)) continue;

    const lane = confirmationWorkDue(b, nowIso);
    const deadline = confirmationDeadlineState(b, nowIso);
    const atRisk = deadline === 'refillable' || deadline === 'deadline-approaching';
    const call = callByBooking.get(b.id);

    const escalated = isConfirmationEscalated(b);
    const staleCall = !b.confirmedAt && call !== undefined && call.outcome === undefined
      && startOfDay(new Date(call.scheduledFor)).getTime() < today0;
    const dueOrPastUnconfirmed = !b.confirmedAt && startOfDay(new Date(b.start)).getTime() <= today0;
    const overdue = escalated || staleCall || dueOrPastUnconfirmed;

    const row: ConfirmationRow = { booking: b, lane, atRisk, overdue, call };

    if (overdue) { chase.push(row); continue; }
    if ((lane === 'seq-1' || lane === 'seq-2' || lane === 'seq-3') && !b.confirmedAt) { calls.push(row); continue; }
    if (lane === 'two-day') { texts.push(row); continue; }
    // Otherwise (incl. confirmed day-of sessions - now the floor's concern):
    // already resolved or nothing due today, so drop from the worklist.
  }

  const byStart = (a: ConfirmationRow, z: ConfirmationRow) => a.booking.start.localeCompare(z.booking.start);
  // `chase` only ever holds overdue/escalated rows (non-overdue at-risk rows stay
  // in their calls/texts lane), so start-time is the effective priority.
  // Roll-forward is emergent: an unresolved booking reappears in chase each day
  // the sim clock advances - no persisted carry-state.
  chase.sort(byStart);
  calls.sort(byStart);
  texts.sort(byStart);

  return {
    chase,
    calls,
    texts,
    counts: {
      total: chase.length + calls.length + texts.length,
      chase: chase.length,
      calls: calls.length,
      texts: texts.length,
    },
  };
}
